import { TrackedAnchor } from "@/components/analytics/TrackedAnchor";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/constants/urls";
import { isAppContext } from "@/lib/appContext";

/** Badge alt text / accessible labels, kept alongside the component they describe. */
const COPY = {
  appStoreAlt: "Download on the App Store",
  googlePlayAlt: "Get it on Google Play",
} as const;

// Intrinsic asset dimensions (App Store badge is an SVG, Google Play badge is
// a PNG). Used below to derive width/height attributes proportioned to each
// call site's rendered height, so the browser can reserve layout space
// (no shift) while `w-auto` keeps the true aspect ratio at any CSS height.
const APP_STORE_INTRINSIC = { width: 119.66, height: 40 };
// Cropped to the visible pill (564x168) — the official export's 41px
// transparent margin was removed (Bill, 8/31) so both badges render the
// same apparent height; required clear space comes from the row gap.
const GOOGLE_PLAY_INTRINSIC = { width: 564, height: 168 };

// Default badge height across call sites (the "Get the App" section uses
// this default; the footer passes its own smaller `badgeHeightClassName`).
// width/height attrs only need to encode the correct *ratio* — browsers
// derive the rendered aspect ratio from them and `w-auto` follows whatever
// CSS height is actually applied — so one proportioned pair here holds for
// every badgeHeightClassName this component is given.
const BADGE_ATTR_HEIGHT = 44;
const appStoreDims = {
  width: Math.round(
    (BADGE_ATTR_HEIGHT * APP_STORE_INTRINSIC.width) / APP_STORE_INTRINSIC.height,
  ),
  height: BADGE_ATTR_HEIGHT,
};
const googlePlayDims = {
  width: Math.round(
    (BADGE_ATTR_HEIGHT * GOOGLE_PLAY_INTRINSIC.width) / GOOGLE_PLAY_INTRINSIC.height,
  ),
  height: BADGE_ATTR_HEIGHT,
};

/**
 * App Store + Google Play badges, wired to `trackCta` via `TrackedAnchor`.
 * Shared between the "Get the App" section (larger, ≥44px tall) and the
 * footer (quieter, ~40px tall) so the order, links, and tracking stay
 * identical everywhere they appear.
 */
export const StoreBadges = ({
  location,
  badgeHeightClassName = "h-11",
  className,
}: {
  location: string;
  badgeHeightClassName?: string;
  className?: string;
}) => {
  if (isAppContext()) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className ?? ""}`}>
      <TrackedAnchor
        ctaId="get_the_app_app_store"
        ctaLocation={location}
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={COPY.appStoreAlt}
        className="shrink-0"
      >
        <img
          src="/images/store-badges/app-store-badge-black.svg"
          alt={COPY.appStoreAlt}
          width={appStoreDims.width}
          height={appStoreDims.height}
          loading="lazy"
          className={`${badgeHeightClassName} w-auto`}
        />
      </TrackedAnchor>
      <TrackedAnchor
        ctaId="get_the_app_google_play"
        ctaLocation={location}
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={COPY.googlePlayAlt}
        className="shrink-0"
      >
        <img
          src="/images/store-badges/google-play-badge.png"
          alt={COPY.googlePlayAlt}
          width={googlePlayDims.width}
          height={googlePlayDims.height}
          loading="lazy"
          className={`${badgeHeightClassName} w-auto`}
        />
      </TrackedAnchor>
    </div>
  );
};
